import { createContext, useContext, useEffect, useState } from 'react'
import { classType, repCodeType } from './types'
import axios from 'axios'

const dataContext = createContext({
  classList: [] as classType[],
  setClassList: (() => {
    return
  }) as React.Dispatch<React.SetStateAction<classType[]>>,
  user: { email: '', admin: false, operations: false, sales: false },
  setUser: (() => {
    return
  }) as React.Dispatch<React.SetStateAction<{ email: string; admin: boolean; operations: boolean; sales: boolean }>>,
  repCodes: [] as repCodeType[],
  setRepCodes: (() => {
    return
  }) as React.Dispatch<React.SetStateAction<repCodeType[]>>,
  invoicingPlatformList: [] as string[],
  setInvoicingPlatformList: (() => {
    return
  }) as React.Dispatch<React.SetStateAction<string[]>>,
  divisionList: [] as string[],
  setDivisionList: (() => {
    return
  }) as React.Dispatch<React.SetStateAction<string[]>>,
  endUserList: [] as string[],
  setEndUserList: (() => {
    return
  }) as React.Dispatch<React.SetStateAction<string[]>>,
})

export function DataProvider({ email, children }: { email: string; children: React.ReactNode }) {
  const [classList, setClassList] = useState<classType[]>([])
  const [user, setUser] = useState({ email: '', admin: false, operations: false, sales: false })
  const [repCodes, setRepCodes] = useState<repCodeType[]>([])
  const [invoicingPlatformList, setInvoicingPlatformList] = useState<string[]>([])
  const [divisionList, setDivisionList] = useState<string[]>([])
  const [endUserList, setEndUserList] = useState<string[]>([])

  useEffect(() => {
    if (!email) return

    setUser(prev => ({ ...prev, email: email }))

    if (process.env.REACT_APP_BACKEND_ENDPOINT !== 'http://localhost:8080') {
      axios
        .get(`/entra/${email}`)
        .then(response => {
          setUser(prev => ({
            ...prev,
            admin: response.data.admin,
            operations: response.data.operations,
            sales: response.data.sales,
          }))
        })
        .catch(err => {
          console.log(err)
        })
    } else {
      setUser(prev => ({ ...prev, admin: true, operations: false, sales: false }))
    }
  }, [email])

  return (
    <dataContext.Provider
      value={{
        classList,
        setClassList,
        user,
        setUser,
        repCodes,
        setRepCodes,
        invoicingPlatformList,
        setInvoicingPlatformList,
        divisionList,
        setDivisionList,
        endUserList,
        setEndUserList,
      }}
    >
      {children}
    </dataContext.Provider>
  )
}

export function useClassList() {
  const { classList, setClassList } = useContext(dataContext)
  useEffect(() => {
    if (classList.length === 0) {
      axios
        .get('/netsuite/classList')
        .then(response => {
          setClassList(response.data)
        })
        .catch(err => {
          console.error(err)
        })
    }
  }, [])
  return classList
}

export function useUser() {
  const { user } = useContext(dataContext)
  return user
}

export function useRepCodes(): [repCodeType[], React.Dispatch<React.SetStateAction<repCodeType[]>>] {
  const { repCodes, setRepCodes } = useContext(dataContext)
  useEffect(() => {
    if (repCodes.length === 0) {
      axios
        .get('/repCodes')
        .then(response => {
          setRepCodes(response.data)
        })
        .catch(err => {
          console.error(err)
        })
    }
  }, [])
  return [repCodes, setRepCodes]
}

export function useInvoicingPlatformList() {
  const { invoicingPlatformList, setInvoicingPlatformList } = useContext(dataContext)
  useEffect(() => {
    if (invoicingPlatformList.length === 0) {
      axios
        .get('/netsuite/invoicingPlatformList')
        .then(response => {
          setInvoicingPlatformList(response.data)
        })
        .catch(err => {
          console.error(err)
        })
    }
  }, [])
  return invoicingPlatformList
}

export function useDivisionList() {
  const { divisionList, setDivisionList } = useContext(dataContext)
  useEffect(() => {
    if (divisionList.length === 0) {
      axios
        .get('/netsuite/segmentList?segment=division')
        .then(response => {
          setDivisionList(response.data.map(({ name }: { name: string }) => name))
        })
        .catch(err => {
          console.error(err)
        })
    }
  }, [])
  return divisionList
}

export function useEndUserList() {
  const { endUserList, setEndUserList } = useContext(dataContext)
  useEffect(() => {
    if (endUserList.length === 0) {
      axios
        .get('/netsuite/segmentList?segment=enduser')
        .then(response => {
          setEndUserList(response.data.map(({ name }: { name: string }) => name))
        })
        .catch(err => {
          console.error(err)
        })
    }
  }, [])
  return endUserList
}
